import getMarketplaceClient from 'util/getMarketplaceClient';
import Progress, { thunkProgress } from 'redux-progress';
import config from 'appconfig';
import { MarketplaceProductKey } from 'modules/marketplace/data';
import { DeploymentOption, CLOUD, DATACENTER } from 'model/hams/DeploymentOption';
import { Dispatch, GetState, State } from 'model/State';
import { checkFeatureGateAsync, FeatureFlags } from 'util/statsig';

type Link = {
  href: string;
  type?: string;
  title?: string;
};

export type MarketplaceAddon = {
  name: string;
  key: string;
  tagLine: string;
  summary: string;
  _links: {
    self: Link;
    alternate: Link;
  };
  _embedded: {
    version?: {
      vendorLinks?: {
        eula?: string;
      };
    };
    logo: {
      _links: {
        self: Link;
        image: Link;
      };
    };
  };
};

export type AddonsMap = {
  [addon: string]: Progress<MarketplaceAddon | null>;
};

export const MARKETPLACE_INFO_FETCH = 'catalog/addons/FETCH';

// Marketplace V3 (Mosaic) integration (CHT-4082): resolves V3 identifiers and
// maps the flattened payloads back into MarketplaceAddon, gated by
// CART_MIGRATION_MKT_V2_TO_MOSAIC_V3_API. appKey -> productId is on the public
// Product Listing service; everything else is on marketplaceUrl under /rest/3.
const PRODUCT_METADATA_PATH = '/rest/3/product-listing/product-metadata/marketplaceAppKey';
const PRODUCT_LISTING_PATH = '/rest/3/product-listing';
const APP_SOFTWARE_PATH = '/rest/3/app-software';

type ProductMetadataResponse = {
  productId: string;
};

type ProductListingResponse = {
  appName?: string;
  appKey?: string;
  tagLine?: string;
  summary?: string;
  // Relative marketplace listing URL, e.g. "/apps/{id}/{slug}?...". TODO(CHT-4082):
  // confirm the exact V3 product-listing field name.
  listingUrl?: string;
  images?: {
    iconFileId?: string;
    titleLogoFileId?: string;
    bannerFileId?: string;
  };
};

type AppSoftwareResponse = {
  appSoftwareId: string;
  hosting?: string;
};

type AppSoftwareVersionResponse = {
  buildNumber: number;
};

type AppSoftwareVersionsResponse = {
  versions?: AppSoftwareVersionResponse[];
};

type AppSoftwareVersionListingResponse = {
  eula?: string;
  developerLinks?: {
    eula?: string;
  };
};

const toMosaicHosting = (hosting: DeploymentOption): string => hosting;

// V3 images are file ids served at {marketplaceUrl}/product-listing/files/{id}
// (72x72 to match the V2 icon). Empty when no file id.
const ICON_SIZE_PX = 72;
const buildImageHref = (fileId?: string): string =>
  fileId
    ? `${config.marketplaceUrl}/product-listing/files/${fileId}?width=${ICON_SIZE_PX}&height=${ICON_SIZE_PX}`
    : '';

// Module-scoped caches (page lifetime) to avoid repeated round-trips.
const productIdCache = new Map<string, Promise<string | null>>();
const appSoftwareIdCache = new Map<string, Promise<string | null>>();

// Exposed for tests to reset module-level caches between cases.
export const clearMosaicCaches = () => {
  productIdCache.clear();
  appSoftwareIdCache.clear();
};

const appSoftwareCacheKey = (appKey: string, hosting: DeploymentOption) => `${appKey}::${hosting}`;

const resolveProductId = (appKey: string): Promise<string | null> => {
  const cached = productIdCache.get(appKey);
  if (cached) {
    return cached;
  }

  const promise = getMarketplaceClient()
    .get<ProductMetadataResponse>(`${PRODUCT_METADATA_PATH}/${encodeURIComponent(appKey)}`, {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    })
    .then((r) => (r.status === 404 ? null : (r.data?.productId ?? null)))
    .catch(() => {
      // Degrade to null on error and drop the cache entry so it can be retried.
      // A single failed hosting must not fail the whole deployment group.
      productIdCache.delete(appKey);
      return null;
    });

  productIdCache.set(appKey, promise);
  return promise;
};

const fetchProductListing = async (productId: string): Promise<ProductListingResponse | null> => {
  const response = await getMarketplaceClient().get<ProductListingResponse>(
    `${PRODUCT_LISTING_PATH}/${encodeURIComponent(productId)}`,
    {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    },
  );
  return response.status === 404 ? null : (response.data ?? null);
};

const resolveAppSoftwareId = (
  appKey: string,
  hosting: DeploymentOption,
): Promise<string | null> => {
  const cacheKey = appSoftwareCacheKey(appKey, hosting);
  const cached = appSoftwareIdCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const mosaicHosting = toMosaicHosting(hosting);
  const promise = getMarketplaceClient()
    .get<AppSoftwareResponse[]>(`${APP_SOFTWARE_PATH}/app-key/${encodeURIComponent(appKey)}`, {
      params: { hosting: mosaicHosting },
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    })
    .then((r) => {
      if (r.status === 404) {
        return null;
      }
      const entries = Array.isArray(r.data) ? r.data : [];
      // Match the requested hosting only. No entry means the app is not sold for
      // this hosting, which must read as "unavailable" (per-hosting availability,
      // equivalent to the V2 per-hosting 404).
      const match = entries.find((entry) => entry.hosting === mosaicHosting);
      return match?.appSoftwareId ?? null;
    })
    .catch(() => {
      // Degrade to null on error; app-software/EULA is optional enrichment.
      appSoftwareIdCache.delete(cacheKey);
      return null;
    });

  appSoftwareIdCache.set(cacheKey, promise);
  return promise;
};

const fetchLatestVersionEula = async (appSoftwareId: string): Promise<string | null> => {
  const versionsResponse = await getMarketplaceClient().get<AppSoftwareVersionsResponse>(
    `${APP_SOFTWARE_PATH}/${encodeURIComponent(appSoftwareId)}/versions`,
    {
      params: { limit: 1, state: 'ACTIVE' },
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    },
  );

  if (versionsResponse.status === 404) {
    return null;
  }

  const latest = versionsResponse.data?.versions?.[0];
  if (!latest || latest.buildNumber == null) {
    return null;
  }

  const listingResponse = await getMarketplaceClient().get<AppSoftwareVersionListingResponse>(
    `${APP_SOFTWARE_PATH}/${encodeURIComponent(appSoftwareId)}/versions/${latest.buildNumber}/listing`,
    {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    },
  );

  if (listingResponse.status === 404) {
    return null;
  }

  const listing = listingResponse.data;
  return listing?.developerLinks?.eula ?? listing?.eula ?? null;
};

const mapMosaicResponseToAddon = (
  appKey: string,
  listing: ProductListingResponse,
  eula: string | null,
): MarketplaceAddon => {
  const imageHref = buildImageHref(listing.images?.iconFileId);

  return {
    name: listing.appName ?? '',
    key: listing.appKey ?? appKey,
    tagLine: listing.tagLine ?? '',
    summary: listing.summary ?? '',
    _links: {
      self: { href: '' },
      // Marketplace listing URL (relative). TODO(CHT-4082): confirm the exact V3
      // product-listing field for this URL against the Product Listing V3 schema.
      alternate: { href: listing.listingUrl ?? '' },
    },
    _embedded: {
      version: {
        vendorLinks: {
          eula: eula ?? undefined,
        },
      },
      logo: {
        _links: {
          self: { href: imageHref },
          image: { href: imageHref },
        },
      },
    },
  };
};

const fetchAddonInfoV3Internal = async (
  appKey: string,
  hosting: DeploymentOption,
): Promise<MarketplaceAddon | null> => {
  const [productId, appSoftwareId] = await Promise.all([
    resolveProductId(appKey),
    resolveAppSoftwareId(appKey, hosting),
  ]);

  // No productId (unknown app) or no app-software for this hosting means the app
  // is not available for this deployment. Returning null keeps that deployment
  // option disabled, preserving V2's per-hosting availability behaviour.
  if (!productId || !appSoftwareId) {
    return null;
  }

  const listing = await fetchProductListing(productId);
  if (!listing) {
    return null;
  }

  const eula = await fetchLatestVersionEula(appSoftwareId);

  return mapMosaicResponseToAddon(appKey, listing, eula);
};

// Addon info is optional enrichment. Any V3 failure must degrade to null so a
// single failing hosting does not fail the whole deployment group (which would
// hide the "Choose your deployment" section).
export const fetchAddonInfoV3 = (
  appKey: string,
  hosting: DeploymentOption,
): Promise<MarketplaceAddon | null> => fetchAddonInfoV3Internal(appKey, hosting).catch(() => null);

export const selectMarketplaceAddon = (
  state: State,
  props: { productKey: string },
): Progress<MarketplaceAddon | null> => state.catalog.addons[props.productKey] || Progress.none;

const getMarketplaceProductKey = (key) => key.replace(/(\.ondemand|\.data-center)$/, '');

const getSuffix = (hosting) => {
  switch (hosting) {
    case DATACENTER:
      return '.data-center';
    case CLOUD:
      return '.ondemand';
    default:
      return '';
  }
};

export const getCommerceProductKey = (key: string, hosting: DeploymentOption) =>
  `${getMarketplaceProductKey(key)}${getSuffix(hosting)}`;

// getCloudVersionKey returns the key for the cloud version of a addon by adding proper prefix
export const getCloudVersionKey = (key: string): string => getCommerceProductKey(key, CLOUD);

export const fetchAddonInfo =
  ({ productKey, hosting }: { productKey: string; hosting: DeploymentOption }) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const productKeyCommerce = getCommerceProductKey(productKey, hosting);
    const state = getState();
    const marketplaceAddon = selectMarketplaceAddon(state, { productKey: productKeyCommerce });
    if (marketplaceAddon.failed || marketplaceAddon.isNone) {
      const isMosaicV3Enabled = await checkFeatureGateAsync(
        FeatureFlags.CART_MIGRATION_MKT_V2_TO_MOSAIC_V3_API,
      );

      const promise = isMosaicV3Enabled
        ? fetchAddonInfoV3(getMarketplaceProductKey(productKey), hosting)
        : getMarketplaceClient()
            .get(
              `/rest/2/addons/${getMarketplaceProductKey(
                productKey,
              )}?withVersion=true&hosting=${hosting}`,
              {
                validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
              },
            )
            .then((r) => (r.status === 404 ? null : r.data));

      await dispatch(
        thunkProgress(MARKETPLACE_INFO_FETCH, promise, {
          payload: { productKey: productKeyCommerce, hosting },
        }),
      );
    }
  };

export const fetchAddonsInfo =
  (items: { productKey: string; hosting: DeploymentOption }[]) => (dispatch: Dispatch) =>
    Promise.all(items.map(fetchAddonInfo).map(dispatch));

export const fetchRecommendedAppsFromMarketplace = (
  application: MarketplaceProductKey,
  limit: number = 5,
) => {
  const params = {
    application,
    limit,
    marketingLabel: 'mac-cart',
    cost: 'marketplace',
  };
  return getMarketplaceClient()
    .get('rest/2/addons', { params }) // eslint-disable-next-line no-underscore-dangle
    .then((r) => r.data._embedded.addons);
};

export type MarketplaceSearchParams = {
  application?: MarketplaceProductKey;
  text: string;
  offset: number;
  limit: number;
};

export const fetchSearchFromMarketplace = (params: MarketplaceSearchParams) =>
  getMarketplaceClient()
    .get('/rest/2/addons', { params }) // eslint-disable-next-line no-underscore-dangle
    .then((r) => r.data._embedded.addons);

const reducer = (state: AddonsMap = {}, action: any) => {
  switch (action.type) {
    case MARKETPLACE_INFO_FETCH:
      return {
        ...state,
        [action.payload.productKey]: action.progress,
      };
    default:
      return state;
  }
};

export default reducer;
